import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { exhaustMap, filter, map, startWith, switchMap, tap } from 'rxjs/operators';

import { FeatureFlagKeys } from '@ninety/platform/models/feature-flag/feature-flag-state.model';
import { UserActivityService } from '@ninety/platform/services/services/user-activity/user-activity.service';

import { selectFeatureFlag } from '../../app-entities/feature-flag/feature-flag-state.selectors';
import { appActions } from '../../app.actions';
import { CONFIRM_DIALOG } from '../../tokens/billing-dialog.tokens';

import { UserActivityActions } from './user-activity.actions';

@Injectable()
export class UserActivityEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly userActivityService = inject(UserActivityService);

  private readonly openConfirm = inject(CONFIRM_DIALOG);

  initUserActivityMonitoring$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.bootstrap),
      switchMap(() =>
        this.userActivityService.activity$.pipe(
          filter(notification => notification.status === 'active'),
          map(notification => UserActivityActions.activityRestored({ notification }))
        )
      )
    )
  );

  /** Shows the inactivity reload prompt once the threshold is crossed. Emits an `opened` action
   *  immediately (via startWith, as the dialog opens) and a `closed` action carrying the user's
   *  choice when it resolves. Both carry hoursInactive (1-decimal, for averaging in analytics).
   *  Behavior (reload) and analytics live in their own effects so this one only owns the dialog. */
  showInactivityReloadPrompt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActivityActions.activityRestored),
      map(
        ({ notification }) => notification.secondsSinceLastChange + this.userActivityService.inactivityTimeoutSeconds
      ),
      filter(totalInactivitySeconds => totalInactivitySeconds >= this.userActivityService.reloadThresholdSeconds),
      concatLatestFrom(() => this.store.select(selectFeatureFlag(FeatureFlagKeys.inactivityDialog))),
      // Kill switch: only an explicit false suppresses the prompt.
      filter(([, dialogEnabled]) => dialogEnabled !== false),
      map(([totalInactivitySeconds]) => totalInactivitySeconds),
      // 1-decimal hours (e.g. 4.7) — a precise numeric averages meaningfully in Gainsight PX,
      // unlike a floored whole-hour value which collapses everything just above the threshold.
      map(totalInactivitySeconds => Math.round((totalInactivitySeconds / 3600) * 10) / 10),
      exhaustMap(hoursInactive =>
        this.openConfirm({
          // Sizes the dialog to 400px via confirm-dialog.scss.
          panelClass: 'session-update-dialog',
          disableClose: true,
          data: {
            title: `We've updated our software`,
            message: `While you were away, we launched an update. Refresh your browser for the best experience.`,
            additionalMessage: `<strong>Note:</strong> If you have unsaved edits, copy them somewhere safe before reloading.`,
            confirmButtonText: 'Reload',
            cancelButtonText: 'Not now',
          },
        })
          .afterClosed()
          .pipe(
            map(shouldReload =>
              UserActivityActions.inactivityReloadPromptClosed({ hoursInactive, shouldReload: !!shouldReload })
            ),
            startWith(UserActivityActions.inactivityReloadPromptOpened({ hoursInactive }))
          )
      )
    )
  );

  reloadOnPromptConfirmed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActivityActions.inactivityReloadPromptClosed),
        filter(({ shouldReload }) => shouldReload),
        tap(() => this.reloadPage())
      ),
    { dispatch: false }
  );

  public reloadPage(): void {
    window.location.reload();
  }
}
